// Mixer graph shared by the live engine and the offline measurement sandbox.
//
//   sfx voices ──► sfxIn ─────────────────────────► sfx (volume) ──┐
//              └─► sfxVerb ─► convolver (short) ──►┘                │
//   music      ──► musicIn ─► musicDuck ─► music (volume) ─────────┤──► master ─► glue comp ─► limiter ─► out
//              └─► musicVerb ─► convolver (long, dark) ─► musicDuck ┘
//
// `musicDuck` is pulled down briefly by heavy sound effects so explosions and
// boss roars cut through the soundtrack.

import { reverbImpulse } from './synth';

export interface MixGraph {
  readonly ctx: BaseAudioContext;
  /** Dry input for sound effects and loops. */
  readonly sfxIn: GainNode;
  /** Reverb send input for sound effects. */
  readonly sfxVerb: GainNode;
  readonly musicIn: GainNode;
  readonly musicVerb: GainNode;
  readonly musicDuck: GainNode;
  /** Volume stages (driven by Settings). */
  readonly sfx: GainNode;
  readonly music: GainNode;
  readonly master: GainNode;
  /** Briefly lower the music by `amount` (0..1) starting at `at`. */
  duck(at: number, amount: number, holdSec?: number): void;
  dispose(): void;
}

/** Settings slider (0..1) → gain, with a perceptual (squared) taper. */
export function sliderGain(v: number): number {
  const c = Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0));
  return c * c;
}

export function createMixGraph(ctx: BaseAudioContext, out: AudioNode = ctx.destination): MixGraph {
  const nodes: AudioNode[] = [];
  const gain = (v: number): GainNode => {
    const g = ctx.createGain();
    g.gain.value = v;
    nodes.push(g);
    return g;
  };

  const master = gain(1);
  const sfx = gain(1);
  const music = gain(1);
  const sfxIn = gain(1);
  const sfxVerb = gain(1);
  const musicIn = gain(1);
  const musicVerb = gain(1);
  const musicDuck = gain(1);

  const sfxConv = ctx.createConvolver();
  sfxConv.buffer = reverbImpulse(ctx, 1.6, 0.55, 0.008);
  const musicConv = ctx.createConvolver();
  musicConv.buffer = reverbImpulse(ctx, 3.2, 0.32, 0.02);
  nodes.push(sfxConv, musicConv);

  // Glue compression, then a fast brick-wall-ish limiter so stacked explosions never clip.
  const glue = ctx.createDynamicsCompressor();
  glue.threshold.value = -16;
  glue.knee.value = 12;
  glue.ratio.value = 3;
  glue.attack.value = 0.006;
  glue.release.value = 0.22;
  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -2;
  limiter.knee.value = 0;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.09;
  // The compressors add automatic make-up gain; trim it back for headroom.
  const trim = gain(0.82);
  nodes.push(glue, limiter);

  sfxIn.connect(sfx);
  sfxVerb.connect(sfxConv);
  sfxConv.connect(sfx);
  musicIn.connect(musicDuck);
  musicVerb.connect(musicConv);
  musicConv.connect(musicDuck);
  musicDuck.connect(music);
  sfx.connect(master);
  music.connect(master);
  master.connect(glue);
  glue.connect(limiter);
  limiter.connect(trim);
  trim.connect(out);

  let duckUntil = 0;
  return {
    ctx,
    sfxIn,
    sfxVerb,
    musicIn,
    musicVerb,
    musicDuck,
    sfx,
    music,
    master,
    duck(at, amount, holdSec = 0.18) {
      const p = musicDuck.gain;
      const floor = Math.max(0.15, 1 - amount);
      p.cancelScheduledValues(at);
      p.setValueAtTime(Math.min(1, p.value), at);
      p.setTargetAtTime(floor, at, 0.025);
      duckUntil = Math.max(duckUntil, at + holdSec);
      p.setTargetAtTime(1, duckUntil, 0.45);
    },
    dispose() {
      for (const n of nodes) {
        try {
          n.disconnect();
        } catch {
          // ignore
        }
      }
    },
  };
}
